import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const products = [
  {
    name: 'RoundCircle',
    tagline: 'Chama & SACCO Management',
    description:
      'Multi-tenant SaaS for group savings. Double-entry accounting, loan management, share tracking, and automated reconciliation — built for the Kenyan market.',
    tags: ['Multi-tenant', 'Double-entry GL', 'M-Pesa', 'Express + MySQL'],
    accent: true,
    emoji: '⭕',
    url: 'https://roundcircle.co.ke',
  },
  {
    name: 'True Cost',
    tagline: 'Subscription Tracker',
    description:
      'Surfaces your actual monthly spend across all active subscriptions. Connect your services, see the real number, cancel what you forgot you had.',
    tags: ['Personal Finance', 'Integrations', 'React SPA'],
    accent: false,
    emoji: '💰',
    url: 'https://truecost.roundcircle.co.ke',
  },
  {
    name: 'Fin-App',
    tagline: 'Personal Finance Companion',
    description:
      'Track income, expenses, and savings goals intelligently. Built for the modern earner juggling multiple income streams.',
    tags: ['Budgeting', 'Goals', 'Analytics'],
    accent: false,
    emoji: '📊',
    url: 'https://fin.roundcircle.co.ke',
  },
];

const ProductsSection = () => {
  return (
    <section id="products" className="py-28 bg-gray-950 border-t-4 border-orange-500">
      <div className="container mx-auto px-6">

        {/* Header */}
        <div className="mb-14">
          <div className="text-xs text-orange-500 font-black uppercase tracking-widest mb-4">
            Our products — built in-house
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-6xl font-black leading-none tracking-tight">
              <span className="text-white">We don't just build</span>
              <br />
              <span
                className="text-transparent"
                style={{ WebkitTextStroke: '2px rgba(255,255,255,0.2)' }}
              >
                for clients.
              </span>
            </h2>
            <p className="text-gray-400 text-base max-w-xs md:text-right leading-relaxed">
              Three live SaaS products proving we eat our own cooking.
            </p>
          </div>
        </div>

        {/* Product cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 forge-product-grid fade-in-up">
          {products.map((product, i) => (
            <motion.a
              key={product.name}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`relative group overflow-hidden card-spotlight card-magnetic block ${
                product.accent
                  ? 'bg-orange-500'
                  : 'bg-gray-900 border border-gray-800 hover:border-orange-500/40'
              }`}
              style={{ borderRadius: '2px', transition: 'border-color 0.25s' }}
              whileHover={{
                y: -5,
                boxShadow: product.accent
                  ? '0 16px 48px rgba(249,115,22,0.4)'
                  : '0 12px 40px rgba(0,0,0,0.5)',
              }}
              transition={{ type: 'spring', stiffness: 280, damping: 24 }}
            >
              <div className="p-8">
                {/* Emoji icon */}
                <div className="text-4xl mb-6">{product.emoji}</div>

                {/* Name + tagline */}
                <div className={`text-xs font-black uppercase tracking-widest mb-2 ${product.accent ? 'text-orange-100/70' : 'text-orange-500'}`}>
                  {product.tagline}
                </div>
                <h3 className={`text-2xl font-black mb-4 uppercase tracking-tight ${product.accent ? 'text-white' : 'text-white'}`}>
                  {product.name}
                </h3>
                <p className={`text-sm leading-relaxed mb-6 ${product.accent ? 'text-orange-100/80' : 'text-gray-400'}`}>
                  {product.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`text-xs font-bold px-2 py-1 ${
                        product.accent
                          ? 'bg-white/15 text-white'
                          : 'bg-gray-800 text-gray-400'
                      }`}
                      style={{ borderRadius: '1px' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Live link arrow */}
              <div
                className={`absolute top-6 right-6 w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                  product.accent ? 'bg-white/20' : 'bg-orange-500'
                }`}
                style={{ borderRadius: '1px' }}
              >
                <ArrowUpRight className={`w-4 h-4 ${product.accent ? 'text-white' : 'text-white'}`} />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
